import { createPortal } from "react-dom";
import { acrylicTokens } from "../theme/acrylic";
import type { GraphPlotTip } from "../utils/graphTooltip";

const t = acrylicTokens;

/** Cap so a long description can't push the tooltip off-screen; the surface
 *  shrinks to its content below this. */
const TOOLTIP_MAX_WIDTH = 600;

/**
 * Acrylic-glass hover tooltip for an embedded graph. Lists every plot on its
 * own line — colour swatch, label (+ description / state), and, for multi-time
 * plots, the start→end window (or "Current" for live plots) — mirroring the C++
 * `tiger_graph::get_tooltip_str` one-line-per-plot tooltip.
 *
 * Rendered through a portal to `document.body` so it isn't clipped by the view
 * panel's `overflow: hidden` and stays above other widgets. Positioned at the
 * cursor point captured on hover (`x`/`y`), clamped to the viewport.
 */
export function GraphTooltip({
  x,
  y,
  plots,
}: {
  x: number;
  y: number;
  plots: GraphPlotTip[];
}) {
  if (plots.length === 0) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1080;
  const maxWidth = Math.min(TOOLTIP_MAX_WIDTH, vw - 16);
  // Width is content-driven (shrink-to-fit), so clamp using the widest possible
  // width to guarantee it stays on-screen regardless of the actual content size.
  const left = Math.max(8, Math.min(x + 14, vw - maxWidth - 8));
  // Flip above the cursor if the tooltip (one line per plot) would overflow the
  // bottom edge.
  const estHeight = 16 + plots.length * 22;
  const top = y + 18 + estHeight > vh ? Math.max(8, y - estHeight - 8) : y + 18;

  const node = (
    <div
      role="tooltip"
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 1300,
        maxWidth,
        background: t.surface,
        backdropFilter: t.surfaceBlur,
        WebkitBackdropFilter: t.surfaceBlur,
        border: t.surfaceBorder,
        borderRadius: t.cardRadius,
        boxShadow: t.surfaceShadow,
        padding: "8px 10px",
        fontFamily: t.font,
        fontSize: 12,
        color: t.textPrimary,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {plots.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
            whiteSpace: "nowrap",
            lineHeight: 1.4,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              flex: "0 0 auto",
              background: p.colour,
              border: "1px solid rgba(0,0,0,0.25)",
            }}
          />
          <span style={{ fontWeight: 600, flex: "0 0 auto" }}>{p.label}</span>
          {p.description ? (
            <span
              style={{
                fontWeight: 400,
                color: t.textSecondary,
                flex: "0 1 auto",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              — {p.description}
            </span>
          ) : null}
          {p.state && p.state !== "DEFAULT" ? (
            <span style={{ fontWeight: 400, color: t.textMuted, flex: "0 0 auto" }}>[{p.state}]</span>
          ) : null}
          {p.timeLabel ? (
            <span
              style={{
                color: t.textSecondary,
                fontVariantNumeric: "tabular-nums",
                flex: "0 0 auto",
                marginLeft: "auto",
                paddingLeft: 12,
              }}
            >
              {p.timeLabel}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
